import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from '../utils/axios';
import {
  Box, Button, Typography, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Chip, Card, CardContent, Tooltip, CircularProgress, Paper,
  Checkbox, Tabs, Tab, Stack, Divider, Menu, ListItemIcon, ListItemText, useTheme, Autocomplete,
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, StickyNote2 as StickerIcon,
  CheckBox as TaskIcon, Business as CompanyIcon, Phone as PhoneIcon, Email as EmailIcon,
  CalendarToday as CalendarIcon, Person as PersonIcon, Close as CloseIcon, MoreVert as MoreIcon,
  Notes as NotesIcon, Archive as ArchiveIcon, DriveFileMove as MoveIcon,
  FileDownload as ExportIcon, ChevronLeft as ChevronLeftIcon, ChevronRight as ChevronRightIcon,
  ContentCopy as CopyIcon, ViewWeek as WeekIcon, ViewKanban as KanbanIcon,
  Today as TodayIcon,
  CalendarMonth as MonthIcon,
} from '@mui/icons-material';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';

const Kanban = () => {
  const { boardName } = useParams();
  const decodedBoardName = decodeURIComponent(boardName);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  
  const { user, isAdmin } = useAuth();
  const [boards, setBoards] = useState([]);
  const [selectedManagerIndex, setSelectedManagerIndex] = useState(0);
  const [cards, setCards] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draggedCard, setDraggedCard] = useState(null);
  
  const [openCardDialog, setOpenCardDialog] = useState(false);
  const [openStickerDialog, setOpenStickerDialog] = useState(false);
  const [openTaskDialog, setOpenTaskDialog] = useState(false);
  const [openNoteDialog, setOpenNoteDialog] = useState(false);
  const [openColumnDialog, setOpenColumnDialog] = useState(false);
  const [openBoardDialog, setOpenBoardDialog] = useState(false);
  const [openEditBoardDialog, setOpenEditBoardDialog] = useState(false);
  const [cardMenuAnchor, setCardMenuAnchor] = useState(null);
  const [menuCard, setMenuCard] = useState(null);
  
  const [selectedCard, setSelectedCard] = useState(null);
  const [selectedColumn, setSelectedColumn] = useState(null);
  
  const defaultCardColor = isDarkMode ? '#1a1f26' : '#ffffff';
  
  const [cardForm, setCardForm] = useState({
    title: '', description: '', company: '', district: '', dealAmount: '', priority: 'средний', color: defaultCardColor, date: new Date().toISOString().split('T')[0]
  });
  const [stickerForm, setStickerForm] = useState({ text: '', color: '#88c0d0' });
  const [taskForm, setTaskForm] = useState({ text: '', dueDate: '' });
  const [editingTask, setEditingTask] = useState(null);
  const [noteForm, setNoteForm] = useState({ text: '' });
  const [columnForm, setColumnForm] = useState({ title: '', color: '#88c0d0' });
  const [boardForm, setBoardForm] = useState({ name: '', manager: '' });
  const [editBoardName, setEditBoardName] = useState('');
  const [openMoveDialog, setOpenMoveDialog] = useState(false);
  const [moveTargetBoard, setMoveTargetBoard] = useState('');
  const [moveTargetColumn, setMoveTargetColumn] = useState('');
  const [cardToMove, setCardToMove] = useState(null);
  
  // Состояние для календаря
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('board'); // 'board' или 'calendar'
  const [calendarViewMode, setCalendarViewMode] = useState('week'); // 'week' или 'month'
  const [calendarData, setCalendarData] = useState([]);
  const [openCopyDialog, setOpenCopyDialog] = useState(false);
  const [copyTargetDate, setCopyTargetDate] = useState('');
  const [cardToCopy, setCardToCopy] = useState(null);
  const [openChangeDateDialog, setOpenChangeDateDialog] = useState(false);
  const [newCardDate, setNewCardDate] = useState('');

  const priorityColors = {
    'низкий': '#a3be8c',
    'средний': '#ebcb8b',
    'высокий': '#d08770',
    'критический': '#bf616a',
  };

  const stickerColors = [
    '#88c0d0', '#5e81ac', '#b48ead', '#a3be8c', '#ebcb8b',
    '#d08770', '#bf616a', '#8fbcbb', '#81a1c1', '#d8dee9'
  ];

  const cardColors = isDarkMode 
    ? [
        '#1a1f26', '#1e2530', '#232a35', '#1f2937', '#1c2128',
        'rgba(136, 192, 208, 0.15)', 'rgba(163, 190, 140, 0.15)', 'rgba(180, 142, 173, 0.15)',
        'rgba(235, 203, 139, 0.15)', 'rgba(208, 135, 112, 0.15)'
      ]
    : [
        '#ffffff', '#f8fafc', '#f1f5f9', '#e2e8f0', '#f0fdf4',
        '#ecfeff', '#fef3c7', '#fce7f3', '#ede9fe', '#fee2e2'
      ];

  const fetchBoards = useCallback(async () => {
    try {
      const response = await axios.get('/api/boards');
      setBoards(response.data);
    } catch (error) {
      console.error('Ошибка загрузки досок:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchBoardData = useCallback(async (boardId, date = null) => {
    try {
      const dateParam = date ? `?date=${date.toISOString().split('T')[0]}` : '';
      const response = await axios.get(`/api/boards/${boardId}${dateParam}`);
      setCards(response.data.cards);
    } catch (error) {
      console.error('Ошибка загрузки данных доски:', error);
    }
  }, []);

  const fetchCalendarData = useCallback(async (boardId, startDate, endDate) => {
    try {
      const response = await axios.get(`/api/boards/${boardId}/calendar`, {
        params: {
          startDate: startDate.toISOString().split('T')[0],
          endDate: endDate.toISOString().split('T')[0]
        }
      });
      setCalendarData(response.data);
    } catch (error) {
      console.error('Ошибка загрузки календаря:', error);
    }
  }, []);

  // Хелперы для работы с датами
  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('ru-RU', { 
      weekday: 'short', 
      day: 'numeric', 
      month: 'short' 
    });
  };

  const formatDateShort = (date) => {
    const d = new Date(date);
    return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
  };

  const isSameDay = (date1, date2) => {
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    return d1.toDateString() === d2.toDateString();
  };

  const getWeekDays = (date) => {
    const curr = new Date(date);
    const week = [];
    curr.setDate(curr.getDate() - curr.getDay() + 1); // Начинаем с понедельника
    for (let i = 0; i < 7; i++) {
      week.push(new Date(curr));
      curr.setDate(curr.getDate() + 1);
    }
    return week;
  };

  const navigateWeek = (direction) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + (direction * 7));
    setSelectedDate(newDate);
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(selectedDate);
    newDate.setMonth(newDate.getMonth() + direction);
    setSelectedDate(newDate);
  };

  const getMonthDays = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];
    
    // Add empty slots for days before the first day of month
    const startDayOfWeek = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1; // Monday = 0
    for (let i = 0; i < startDayOfWeek; i++) {
      const prevDate = new Date(year, month, -startDayOfWeek + i + 1);
      days.push({ date: prevDate, isCurrentMonth: false });
    }
    
    // Add all days of the month
    for (let day = 1; day <= lastDay.getDate(); day++) {
      days.push({ date: new Date(year, month, day), isCurrentMonth: true });
    }
    
    // Add days to complete the last week
    const remainingDays = 7 - (days.length % 7);
    if (remainingDays < 7) {
      for (let i = 1; i <= remainingDays; i++) {
        days.push({ date: new Date(year, month + 1, i), isCurrentMonth: false });
      }
    }
    
    return days;
  };

  const navigateDay = (direction) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + direction);
    setSelectedDate(newDate);
  };

  const goToToday = () => {
    setSelectedDate(new Date());
  };

  const fetchCompanies = useCallback(async () => {
    try {
      const response = await axios.get('/api/companies');
      setCompanies(response.data);
    } catch (error) {
      console.error('Ошибка загрузки компаний:', error);
    }
  }, []);

  const [managers, setManagers] = useState([]);

  const fetchManagers = useCallback(async () => {
    try {
      const response = await axios.get('/api/users/managers');
      setManagers(response.data);
    } catch (error) {
      console.error('Ошибка загрузки менеджеров:', error);
    }
  }, []);

  useEffect(() => {
    fetchBoards();
    fetchCompanies();
    if (isAdmin) {
      fetchManagers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const boardsWithCurrentName = boards.filter(board => board.name === decodedBoardName);
  
  // Автоматически выбираем правильного менеджера при открытии доски
  useEffect(() => {
    if (isAdmin && boards.length > 0 && managers.length > 0 && decodedBoardName) {
      // Находим доску с таким именем
      const boardWithName = boards.find(board => board.name === decodedBoardName);
      if (boardWithName && boardWithName.manager) {
        const boardManagerId = typeof boardWithName.manager === 'object' 
          ? boardWithName.manager._id 
          : boardWithName.manager;
        // Находим индекс этого менеджера в списке
        const managerIndex = managers.findIndex(m => m._id === boardManagerId);
        if (managerIndex !== -1 && managerIndex !== selectedManagerIndex) {
          setSelectedManagerIndex(managerIndex);
        }
      }
    }
  }, [boards, managers, decodedBoardName, isAdmin]);

  const selectedManager = isAdmin ? managers[selectedManagerIndex] : null;
  
  let selectedBoard;
  if (isAdmin) {
    // Для админа - ищем доску выбранного менеджера (сначала с текущим именем, потом любую)
    selectedBoard = selectedManager 
      ? boardsWithCurrentName.find(board => {
          if (!board.manager) return false;
          const managerId = typeof board.manager === 'object' ? board.manager._id : board.manager;
          return managerId === selectedManager._id;
        }) || boards.find(board => {
          // Если доски с таким именем нет, берём первую доску этого менеджера
          if (!board.manager) return false;
          const managerId = typeof board.manager === 'object' ? board.manager._id : board.manager;
          return managerId === selectedManager._id;
        })
      : null;
  } else {
    selectedBoard = boardsWithCurrentName.find(board => {
      if (!board.manager) return false;
      const managerId = typeof board.manager === 'object' ? board.manager._id : board.manager;
      return managerId === user?.id;
    });
  }

  // Обработчик переключения менеджера - просто меняем выбранного менеджера
  const handleManagerChange = useCallback((event, newIndex) => {
    setSelectedManagerIndex(newIndex);
  }, []);

  useEffect(() => {
    if (selectedBoard) {
      fetchBoardData(selectedBoard._id, selectedDate);
      // Загружаем данные календаря в зависимости от режима
      if (calendarViewMode === 'month') {
        const monthDays = getMonthDays(selectedDate);
        fetchCalendarData(selectedBoard._id, monthDays[0].date, monthDays[monthDays.length - 1].date);
      } else {
        const weekDays = getWeekDays(selectedDate);
        fetchCalendarData(selectedBoard._id, weekDays[0], weekDays[6]);
      }
    }
  }, [selectedBoard, selectedDate, calendarViewMode, fetchBoardData, fetchCalendarData]);

  useEffect(() => {
    const handleDeleteCurrentBoard = async () => {
      if (decodedBoardName && boardsWithCurrentName.length > 0) {
        if (window.confirm(`Удалить доску "${decodedBoardName}" для всех менеджеров? Все карточки будут удалены.`)) {
          try {
            await Promise.all(
              boardsWithCurrentName.map(board => axios.delete(`/api/boards/${board._id}`))
            );
            window.dispatchEvent(new CustomEvent('refreshBoards'));
            window.location.href = '/';
          } catch (error) {
            console.error('Ошибка удаления доски:', error);
          }
        }
      }
    };

    const handleRefreshBoards = () => {
      fetchBoards();
    };

    window.addEventListener('deleteCurrentBoard', handleDeleteCurrentBoard);
    window.addEventListener('refreshBoards', handleRefreshBoards);

    return () => {
      window.removeEventListener('deleteCurrentBoard', handleDeleteCurrentBoard);
      window.removeEventListener('refreshBoards', handleRefreshBoards);
    };
  }, [decodedBoardName, boardsWithCurrentName, fetchBoards]);

  const handleOpenCardDialog = (columnId, card = null) => {
    setSelectedColumn(columnId);
    if (card) {
      setSelectedCard(card);
      setCardForm({
        title: card.title || '',
        description: card.description || '',
        company: card.company._id,
        district: card.district || '',
        dealAmount: card.dealAmount || '',
        priority: card.priority,
        color: card.color || defaultCardColor,
        date: card.date ? card.date.split('T')[0] : selectedDate.toISOString().split('T')[0]
      });
    } else {
      setSelectedCard(null);
      setCardForm({
        title: '', description: '', company: '', district: '', dealAmount: '', priority: 'средний', color: defaultCardColor, date: selectedDate.toISOString().split('T')[0]
      });
    }
    setOpenCardDialog(true);
  };

  const handleSaveCard = async () => {
    try {
      if (selectedCard) {
        await axios.patch(`/api/cards/${selectedCard._id}`, cardForm);
      } else {
        await axios.post('/api/cards', {
          ...cardForm,
          board: selectedBoard._id,
          columnId: selectedColumn
        });
      }
      await fetchBoardData(selectedBoard._id, selectedDate);
      setOpenCardDialog(false);
    } catch (error) {
      console.error('Ошибка сохранения карточки:', error);
    }
  };

  const handleDeleteCard = async (cardId) => {
    setCardMenuAnchor(null);
    if (window.confirm('Удалить карточку?')) {
      try {
        await axios.delete(`/api/cards/${cardId}`);
        setCards(prevCards => prevCards.filter(c => c._id !== cardId));
      } catch (error) {
        console.error('Ошибка удаления карточки:', error);
      }
    }
  };

  const handleOpenCardMenu = (event, card) => {
    event.stopPropagation();
    setCardMenuAnchor(event.currentTarget);
    setMenuCard(card);
  };

  const handleCloseCardMenu = () => {
    setCardMenuAnchor(null);
    setMenuCard(null);
  };

  const handleEditFromMenu = () => {
    if (menuCard) {
      handleOpenCardDialog(menuCard.columnId, menuCard);
    }
    handleCloseCardMenu();
  };

  const handleDeleteFromMenu = () => {
    if (menuCard) {
      handleDeleteCard(menuCard._id);
    }
  };

  const handleDragStart = (e, card) => {
    setDraggedCard(card);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = async (e, columnId) => {
    e.preventDefault();
    if (!draggedCard || draggedCard.columnId === columnId) {
      setDraggedCard(null);
      return;
    }

    try {
      const columnCards = cards.filter(c => c.columnId === columnId);
      await axios.post(`/api/cards/${draggedCard._id}/move`, {
        columnId,
        position: columnCards.length
      });
      await fetchBoardData(selectedBoard._id, selectedDate);
    } catch (error) {
      console.error('Ошибка перемещения карточки:', error);
    }

    setDraggedCard(null);
  };

  const handleAddSticker = async () => {
    try {
      await axios.post(`/api/cards/${selectedCard._id}/stickers`, stickerForm);
      await fetchBoardData(selectedBoard._id, selectedDate);
      setOpenStickerDialog(false);
      setStickerForm({ text: '', color: '#88c0d0' });
    } catch (error) {
      console.error('Ошибка добавления стикера:', error);
    }
  };

  const handleDeleteSticker = async (cardId, stickerId) => {
    try {
      await axios.delete(`/api/cards/${cardId}/stickers/${stickerId}`);
      await fetchBoardData(selectedBoard._id, selectedDate);
    } catch (error) {
      console.error('Ошибка удаления стикера:', error);
    }
  };

  const handleAddTask = async () => {
    try {
      if (editingTask) {
        // Редактирование существующей задачи
        await axios.patch(`/api/cards/${selectedCard._id}/tasks/${editingTask._id}`, taskForm);
      } else {
        // Добавление новой задачи
        await axios.post(`/api/cards/${selectedCard._id}/tasks`, taskForm);
      }
      await fetchBoardData(selectedBoard._id, selectedDate);
      setOpenTaskDialog(false);
      setTaskForm({ text: '', dueDate: '' });
      setEditingTask(null);
    } catch (error) {
      console.error('Ошибка сохранения задачи:', error);
    }
  };

  const handleOpenEditTask = (card, task) => {
    setSelectedCard(card);
    setEditingTask(task);
    setTaskForm({ 
      text: task.text, 
      dueDate: task.dueDate ? task.dueDate.split('T')[0] : '' 
    });
    setOpenTaskDialog(true);
  };

  const handleToggleTask = async (card, taskId, completed) => {
    try {
      await axios.patch(`/api/cards/${card._id}/tasks/${taskId}`, { completed: !completed });
      await fetchBoardData(selectedBoard._id, selectedDate);
    } catch (error) {
      console.error('Ошибка обновления задачи:', error);
    }
  };

  const handleDeleteTask = async (cardId, taskId) => {
    try {
      await axios.delete(`/api/cards/${cardId}/tasks/${taskId}`);
      await fetchBoardData(selectedBoard._id, selectedDate);
    } catch (error) {
      console.error('Ошибка удаления задачи:', error);
    }
  };

  const handleAddNote = async () => {
    try {
      await axios.post(`/api/cards/${selectedCard._id}/notes`, noteForm);
      await fetchBoardData(selectedBoard._id, selectedDate);
      setOpenNoteDialog(false);
      setNoteForm({ text: '' });
    } catch (error) {
      console.error('Ошибка добавления заметки:', error);
    }
  };

  const handleDeleteNote = async (cardId, noteId) => {
    try {
      await axios.delete(`/api/cards/${cardId}/notes/${noteId}`);
      await fetchBoardData(selectedBoard._id, selectedDate);
    } catch (error) {
      console.error('Ошибка удаления заметки:', error);
    }
  };

  const handleAddColumn = async () => {
    try {
      const newColumn = {
        id: `col-${Date.now()}`,
        title: columnForm.title,
        color: columnForm.color,
        order: selectedBoard.columns.length
      };
      
      await axios.patch(`/api/boards/${selectedBoard._id}`, {
        columns: [...selectedBoard.columns, newColumn]
      });
      
      await fetchBoards();
      setOpenColumnDialog(false);
      setColumnForm({ title: '', color: '#88c0d0' });
    } catch (error) {
      console.error('Ошибка добавления колонки:', error);
    }
  };

  const handleDeleteColumn = async (columnId) => {
    if (window.confirm('Удалить колонку и все карточки в ней?')) {
      try {
        const updatedColumns = selectedBoard.columns.filter(col => col.id !== columnId);
        await axios.patch(`/api/boards/${selectedBoard._id}`, { columns: updatedColumns });
        
        const columnCards = cards.filter(c => c.columnId === columnId);
        await Promise.all(columnCards.map(card => axios.delete(`/api/cards/${card._id}`)));
        
        await fetchBoards();
        await fetchBoardData(selectedBoard._id, selectedDate);
      } catch (error) {
        console.error('Ошибка удаления колонки:', error);
      }
    }
  };

  const handleCreateBoard = async () => {
    try {
      if (!selectedManager) {
        alert('Выберите менеджера');
        return;
      }

      await axios.post('/api/boards', {
        name: boardForm.name,
        manager: selectedManager._id,
        columns: [
          { id: 'col-1', title: 'Первое касание', color: '#88c0d0', order: 0 },
          { id: 'col-2', title: 'Коммерческое предложение', color: '#a3be8c', order: 1 },
          { id: 'col-3', title: 'Ждем заключения', color: '#ebcb8b', order: 2 },
          { id: 'col-4', title: 'Успешные', color: '#a3be8c', order: 3 },
          { id: 'col-5', title: 'Отказы', color: '#bf616a', order: 4 }
        ]
      });
      await fetchBoards();
      setOpenBoardDialog(false);
      setBoardForm({ name: '', manager: '' });
    } catch (error) {
      console.error('Ошибка создания доски:', error);
    }
  };

  const handleRenameBoard = async () => {
    try {
      await axios.patch(`/api/boards/${selectedBoard._id}`, {
        name: editBoardName
      });
      await fetchBoards();
      setOpenEditBoardDialog(false);
    } catch (error) {
      console.error('Ошибка переименования доски:', error);
    }
  };

  const handleOpenMoveDialog = () => {
    if (menuCard) {
      setCardToMove(menuCard);
      setMoveTargetBoard('');
      setMoveTargetColumn('');
      setOpenMoveDialog(true);
      handleCloseCardMenu();
    }
  };

  // Копирование карточки на другую дату
  const handleOpenCopyDialog = () => {
    if (menuCard) {
      setCardToCopy(menuCard);
      setCopyTargetDate(new Date().toISOString().split('T')[0]);
      setOpenCopyDialog(true);
      handleCloseCardMenu();
    }
  };

  const handleCopyCard = async () => {
    if (!cardToCopy || !copyTargetDate) return;
    
    try {
      await axios.post(`/api/cards/${cardToCopy._id}/copy`, {
        date: copyTargetDate
      });
      
      await fetchBoardData(selectedBoard._id, selectedDate);
      setOpenCopyDialog(false);
      setCardToCopy(null);
    } catch (error) {
      console.error('Ошибка копирования карточки:', error);
      alert('Ошибка при копировании карточки');
    }
  };

  // Изменение даты карточки
  const handleOpenChangeDateDialog = () => {
    if (menuCard) {
      setCardToCopy(menuCard);
      setNewCardDate(menuCard.date ? menuCard.date.split('T')[0] : '');
      setOpenChangeDateDialog(true);
      handleCloseCardMenu();
    }
  };

  const handleChangeCardDate = async () => {
    if (!cardToCopy || !newCardDate) return;
    
    try {
      await axios.patch(`/api/cards/${cardToCopy._id}/date`, {
        date: newCardDate
      });
      
      await fetchBoardData(selectedBoard._id, selectedDate);
      setOpenChangeDateDialog(false);
      setCardToCopy(null);
    } catch (error) {
      console.error('Ошибка изменения даты:', error);
      alert('Ошибка при изменении даты');
    }
  };

  const handleMoveCard = async () => {
    if (!cardToMove || !moveTargetBoard || !moveTargetColumn) return;
    
    try {
      await axios.post(`/api/cards/${cardToMove._id}/move`, {
        boardId: moveTargetBoard,
        columnId: moveTargetColumn,
        position: 0
      });
      
      if (selectedBoard) {
        await fetchBoardData(selectedBoard._id, selectedDate);
      }
      
      setOpenMoveDialog(false);
      setCardToMove(null);
    } catch (error) {
      console.error('Ошибка переноса карточки:', error);
      alert('Ошибка при переносе карточки');
    }
  };

  const getAvailableBoardsForMove = () => {
    if (!selectedBoard) return [];
    return boards.filter(b => b._id !== selectedBoard._id);
  };

  const getTargetBoardColumns = () => {
    if (!moveTargetBoard) return [];
    const targetBoard = boards.find(b => b._id === moveTargetBoard);
    return targetBoard ? targetBoard.columns : [];
  };

  if (loading) {
    return (
      <Layout>
        <Box 
          display="flex" 
          justifyContent="center" 
          alignItems="center" 
          minHeight="60vh"
          flexDirection="column"
          gap={2}
        >
          <CircularProgress size={60} thickness={2} />
          <Typography variant="body2" color="text.secondary">
            Загрузка доски...
          </Typography>
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box sx={{ mb: 3 }}>
        {/* Manager tabs - admin only */}
        {isAdmin && managers.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: 'text.secondary' }}>
              Менеджеры
            </Typography>
            <Tabs
              value={selectedManagerIndex}
              onChange={handleManagerChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                '& .MuiTabs-indicator': {
                  background: 'linear-gradient(90deg, #88c0d0, #b48ead)',
                  height: 3,
                  borderRadius: 2,
                },
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontSize: '1rem',
                  fontWeight: 600,
                  minHeight: 56,
                  px: 3,
                  color: 'text.secondary',
                  '&.Mui-selected': {
                    color: isDarkMode ? '#88c0d0' : '#5e81ac',
                  }
                }
              }}
            >
              {managers.map((manager, index) => (
                <Tab
                  key={manager._id}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: 2,
                          background: selectedManagerIndex === index 
                            ? 'linear-gradient(135deg, #88c0d0 0%, #5e81ac 100%)'
                            : isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.8rem',
                          fontWeight: 700,
                          color: selectedManagerIndex === index ? '#0d1117' : 'inherit',
                        }}
                      >
                        {manager.firstName[0]}{manager.lastName[0]}
                      </Box>
                      <Typography variant="body1" fontWeight={600}>
                        {manager.firstName} {manager.lastName}
                      </Typography>
                    </Box>
                  }
                />
              ))}
            </Tabs>
          </Box>
        )}

        {/* Header for manager */}
        {!isAdmin && selectedBoard && (
          <Box sx={{ mb: 2 }}>
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 800,
                background: isDarkMode 
                  ? 'linear-gradient(135deg, #e6edf3 0%, #88c0d0 100%)'
                  : 'linear-gradient(135deg, #2e3440 0%, #5e81ac 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              {decodedBoardName}
            </Typography>
          </Box>
        )}

        {/* Панель выбора даты и режима */}
        {selectedBoard && (
          <Paper
            sx={{
              p: 2,
              mb: 3,
              background: isDarkMode ? 'rgba(22, 27, 34, 0.8)' : '#ffffff',
              borderRadius: 3,
              border: '1px solid',
              borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.08)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
              {/* Переключатель неделя/месяц */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Button
                  variant={calendarViewMode === 'week' ? 'contained' : 'outlined'}
                  size="small"
                  startIcon={<WeekIcon />}
                  onClick={() => setCalendarViewMode('week')}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    ...(calendarViewMode === 'week' && {
                      background: 'linear-gradient(135deg, #88c0d0 0%, #5e81ac 100%)',
                    })
                  }}
                >
                  Неделя
                </Button>
                <Button
                  variant={calendarViewMode === 'month' ? 'contained' : 'outlined'}
                  size="small"
                  startIcon={<MonthIcon />}
                  onClick={() => setCalendarViewMode('month')}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    ...(calendarViewMode === 'month' && {
                      background: 'linear-gradient(135deg, #88c0d0 0%, #5e81ac 100%)',
                    })
                  }}
                >
                  Месяц
                </Button>
              </Box>

              {/* Навигация по дням */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Tooltip title={calendarViewMode === 'month' ? "Предыдущий месяц" : "Предыдущая неделя"}>
                  <IconButton onClick={() => calendarViewMode === 'month' ? navigateMonth(-1) : navigateWeek(-1)} size="small">
                    <ChevronLeftIcon />
                  </IconButton>
                </Tooltip>
                
                {calendarViewMode === 'week' ? (
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {getWeekDays(selectedDate).map((day, index) => {
                      const isSelected = isSameDay(day, selectedDate);
                      const isToday = isSameDay(day, new Date());
                      const dayData = calendarData.find(d => d.date === day.toISOString().split('T')[0]);
                      const hasCards = dayData && dayData.stats.companies > 0;
                      
                      return (
                        <Box
                          key={index}
                          onClick={() => setSelectedDate(day)}
                          sx={{
                            minWidth: 70,
                            p: 1,
                            borderRadius: 2,
                            cursor: 'pointer',
                            textAlign: 'center',
                            transition: 'all 0.2s',
                            background: isSelected 
                              ? 'linear-gradient(135deg, #88c0d0 0%, #5e81ac 100%)'
                              : isToday 
                                ? isDarkMode ? 'rgba(163, 190, 140, 0.2)' : 'rgba(163, 190, 140, 0.15)'
                                : 'transparent',
                            border: '2px solid',
                            borderColor: isSelected 
                              ? '#88c0d0' 
                              : isToday 
                                ? '#a3be8c' 
                                : 'transparent',
                            '&:hover': {
                              background: isSelected 
                                ? 'linear-gradient(135deg, #88c0d0 0%, #5e81ac 100%)'
                                : isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)',
                            }
                          }}
                        >
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              display: 'block',
                              fontWeight: 600,
                              color: isSelected ? '#0d1117' : 'text.secondary',
                              textTransform: 'uppercase',
                              fontSize: '0.65rem'
                            }}
                          >
                            {day.toLocaleDateString('ru-RU', { weekday: 'short' })}
                          </Typography>
                          <Typography 
                            variant="h6" 
                            sx={{ 
                              fontWeight: 700,
                              color: isSelected ? '#0d1117' : 'text.primary',
                              lineHeight: 1.2
                            }}
                          >
                            {day.getDate()}
                          </Typography>
                          {hasCards && (
                            <Box sx={{ 
                              display: 'flex', 
                              justifyContent: 'center', 
                              gap: 0.5, 
                              mt: 0.5 
                            }}>
                              <Chip 
                                label={dayData.stats.companies} 
                                size="small"
                                sx={{ 
                                  height: 18, 
                                  fontSize: '0.65rem',
                                  background: isSelected ? 'rgba(0,0,0,0.2)' : '#88c0d0',
                                  color: isSelected ? '#0d1117' : '#fff',
                                }} 
                              />
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                ) : (
                  <Typography variant="h6" sx={{ fontWeight: 700, minWidth: 200, textAlign: 'center' }}>
                    {selectedDate.toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' })}
                  </Typography>
                )}

                <Tooltip title={calendarViewMode === 'month' ? "Следующий месяц" : "Следующая неделя"}>
                  <IconButton onClick={() => calendarViewMode === 'month' ? navigateMonth(1) : navigateWeek(1)} size="small">
                    <ChevronRightIcon />
                  </IconButton>
                </Tooltip>

                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

                <Tooltip title="Сегодня">
                  <IconButton onClick={goToToday} color="primary" size="small">
                    <TodayIcon />
                  </IconButton>
                </Tooltip>
                
                <TextField
                  type="date"
                  size="small"
                  value={selectedDate.toISOString().split('T')[0]}
                  onChange={(e) => setSelectedDate(new Date(e.target.value))}
                  sx={{ width: 150 }}
                  InputProps={{
                    sx: { borderRadius: 2 }
                  }}
                />
              </Box>

              {/* Переключатель режимов */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Button
                  variant={viewMode === 'board' ? 'contained' : 'outlined'}
                  size="small"
                  startIcon={<KanbanIcon />}
                  onClick={() => setViewMode('board')}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    ...(viewMode === 'board' && {
                      background: 'linear-gradient(135deg, #88c0d0 0%, #5e81ac 100%)',
                    })
                  }}
                >
                  Доска
                </Button>
                <Button
                  variant={viewMode === 'calendar' ? 'contained' : 'outlined'}
                  size="small"
                  startIcon={<WeekIcon />}
                  onClick={() => setViewMode('calendar')}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    ...(viewMode === 'calendar' && {
                      background: 'linear-gradient(135deg, #88c0d0 0%, #5e81ac 100%)',
                    })
                  }}
                >
                  Календарь
                </Button>
              </Box>
            </Box>
          </Paper>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2, mb: 2 }}>
          {isAdmin && selectedBoard && (
            <Button
              variant="contained"
              startIcon={<ExportIcon />}
              onClick={async () => {
                try {
                  const response = await axios.get(
                    `/api/boards/export-by-name/${encodeURIComponent(decodedBoardName)}`,
                    { responseType: 'blob' }
                  );
                  
                  const url = window.URL.createObjectURL(new Blob([response.data]));
                  const link = document.createElement('a');
                  link.href = url;
                  link.setAttribute('download', `${decodedBoardName}_Отчет_${new Date().toISOString().split('T')[0]}.xlsx`);
                  document.body.appendChild(link);
                  link.click();
                  link.remove();
                  window.URL.revokeObjectURL(url);
                } catch (error) {
                  console.error('Ошибка экспорта:', error);
                  alert('Ошибка при экспорте данных');
                }
              }}
              sx={{
                background: 'linear-gradient(135deg, #a3be8c 0%, #88c0d0 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #88c0d0 0%, #a3be8c 100%)',
                }
              }}
            >
              Экспорт в Excel
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => setOpenColumnDialog(true)}
            disabled={!selectedBoard}
          >
            Добавить колонку
          </Button>
        </Box>
      </Box>

      {isAdmin && managers.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" sx={{ color: 'text.secondary' }}>
            Нет одобренных менеджеров
          </Typography>
        </Box>
      ) : boards.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          {isAdmin ? (
            <>
              <Typography 
                variant="h4" 
                sx={{ 
                  fontWeight: 800, 
                  mb: 2,
                  background: 'linear-gradient(135deg, #88c0d0 0%, #b48ead 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Создать доски для всех менеджеров
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary', mb: 4 }}>
                Создайте доски для всех одобренных менеджеров одним нажатием
              </Typography>
              <Button
                variant="contained"
                size="large"
                startIcon={<AddIcon />}
                onClick={async () => {
                  try {
                    setLoading(true);
                    await Promise.all(managers.map(manager => 
                      axios.post('/api/boards', {
                        name: `Воронка продаж - ${manager.firstName} ${manager.lastName}`,
                        manager: manager._id,
                        columns: [
                          { id: 'col-1', title: 'Первое касание', color: '#88c0d0', order: 0 },
                          { id: 'col-2', title: 'Коммерческое предложение', color: '#a3be8c', order: 1 },
                          { id: 'col-3', title: 'Ждем заключения', color: '#ebcb8b', order: 2 },
                          { id: 'col-4', title: 'Успешные', color: '#a3be8c', order: 3 },
                          { id: 'col-5', title: 'Отказы', color: '#bf616a', order: 4 }
                        ]
                      })
                    ));
                    await fetchBoards();
                    setLoading(false);
                  } catch (error) {
                    console.error('Ошибка создания досок:', error);
                    setLoading(false);
                  }
                }}
              >
                Создать доски
              </Button>
            </>
          ) : (
            <Typography variant="h6" sx={{ color: 'text.secondary' }}>
              У вас пока нет досок. Обратитесь к администратору для создания доски.
            </Typography>
          )}
        </Box>
      ) : boardsWithCurrentName.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" sx={{ color: 'text.secondary' }}>
            Доска "{decodedBoardName}" не найдена
          </Typography>
        </Box>
      ) : !selectedBoard ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" sx={{ color: 'text.secondary' }}>
            {isAdmin 
              ? `У менеджера нет доски "${decodedBoardName}"`
              : `У вас нет доски "${decodedBoardName}"`
            }
          </Typography>
        </Box>
      ) : selectedBoard ? (
        viewMode === 'calendar' ? (
          // Режим календаря - недельный или месячный обзор
          <Paper
            sx={{
              p: 3,
              background: isDarkMode ? 'rgba(22, 27, 34, 0.6)' : '#ffffff',
              borderRadius: 3,
              border: '1px solid',
              borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.08)',
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
              {calendarViewMode === 'month' 
                ? selectedDate.toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' })
                : `Обзор недели: ${getWeekDays(selectedDate)[0].toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' })} - ${getWeekDays(selectedDate)[6].toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })}`
              }
            </Typography>
            
            {/* Заголовки дней недели для месячного вида */}
            {calendarViewMode === 'month' && (
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1, mb: 1 }}>
                {['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'].map((dayName) => (
                  <Typography 
                    key={dayName} 
                    variant="caption" 
                    sx={{ 
                      textAlign: 'center', 
                      fontWeight: 700, 
                      color: 'text.secondary',
                      textTransform: 'uppercase',
                      py: 1
                    }}
                  >
                    {dayName}
                  </Typography>
                ))}
              </Box>
            )}
            
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(7, 1fr)', 
              gap: calendarViewMode === 'month' ? 1 : 2 
            }}>
              {(calendarViewMode === 'month' ? getMonthDays(selectedDate) : getWeekDays(selectedDate).map(d => ({ date: d, isCurrentMonth: true }))).map((dayObj, index) => {
                const day = dayObj.date;
                const isCurrentMonth = dayObj.isCurrentMonth;
                const dateKey = day.toISOString().split('T')[0];
                const dayData = calendarData.find(d => d.date === dateKey);
                const isToday = isSameDay(day, new Date());
                const isSelected = isSameDay(day, selectedDate);
                
                return (
                  <Paper
                    key={index}
                    onClick={() => {
                      setSelectedDate(day);
                      setViewMode('board');
                    }}
                    sx={{
                      p: calendarViewMode === 'month' ? 1.5 : 2,
                      minHeight: calendarViewMode === 'month' ? 100 : 200,
                      cursor: 'pointer',
                      opacity: isCurrentMonth ? 1 : 0.4,
                      background: isToday 
                        ? isDarkMode ? 'rgba(163, 190, 140, 0.1)' : 'rgba(163, 190, 140, 0.08)'
                        : isDarkMode ? 'rgba(30, 37, 48, 0.5)' : '#f8fafc',
                      border: '2px solid',
                      borderColor: isToday ? '#a3be8c' : isSelected ? '#88c0d0' : 'transparent',
                      borderRadius: 2,
                      transition: 'all 0.2s',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: isDarkMode 
                          ? '0 8px 24px rgba(0, 0, 0, 0.4)'
                          : '0 8px 24px rgba(0, 0, 0, 0.12)',
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: calendarViewMode === 'month' ? 0.5 : 1.5 }}>
                      {calendarViewMode === 'week' && (
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            fontWeight: 600, 
                            textTransform: 'uppercase',
                            color: isToday ? '#a3be8c' : 'text.secondary'
                          }}
                        >
                          {day.toLocaleDateString('ru-RU', { weekday: 'short' })}
                        </Typography>
                      )}
                      <Typography 
                        variant={calendarViewMode === 'month' ? 'body1' : 'h5'}
                        sx={{ 
                          fontWeight: 700,
                          color: isToday ? '#a3be8c' : 'text.primary',
                          ml: calendarViewMode === 'month' ? 'auto' : 0
                        }}
                      >
                        {day.getDate()}
                      </Typography>
                    </Box>
                    
                    {dayData ? (
                      <Box>
                        {calendarViewMode === 'week' ? (
                          <>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <CompanyIcon sx={{ fontSize: 16, color: '#88c0d0' }} />
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {dayData.stats.companies} компан{dayData.stats.companies === 1 ? 'ия' : dayData.stats.companies < 5 ? 'ии' : 'ий'}
                              </Typography>
                            </Box>
                            
                            {dayData.stats.tasks > 0 && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <TaskIcon sx={{ fontSize: 14, color: '#a3be8c' }} />
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                  {dayData.stats.completedTasks}/{dayData.stats.tasks} задач
                                </Typography>
                              </Box>
                            )}
                            
                            {dayData.stats.stickers > 0 && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <StickerIcon sx={{ fontSize: 14, color: '#ebcb8b' }} />
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                  {dayData.stats.stickers} стикер{dayData.stats.stickers === 1 ? '' : dayData.stats.stickers < 5 ? 'а' : 'ов'}
                                </Typography>
                              </Box>
                            )}
                            
                            {dayData.stats.notes > 0 && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <NotesIcon sx={{ fontSize: 14, color: '#d08770' }} />
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                  {dayData.stats.notes} замет{dayData.stats.notes === 1 ? 'ка' : dayData.stats.notes < 5 ? 'ки' : 'ок'}
                                </Typography>
                              </Box>
                            )}
                            
                            <Divider sx={{ my: 1 }} />
                            
                            <Box sx={{ maxHeight: 80, overflow: 'auto' }}>
                              {dayData.cards.slice(0, 3).map((card, idx) => (
                                <Typography 
                                  key={idx} 
                                  variant="caption" 
                                  sx={{ 
                                    display: 'block', 
                                    color: 'text.secondary',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    mb: 0.3
                                  }}
                                >
                                  • {card.company}
                                </Typography>
                              ))}
                              {dayData.cards.length > 3 && (
                                <Typography variant="caption" sx={{ color: '#88c0d0', fontWeight: 600 }}>
                                  +{dayData.cards.length - 3} ещё
                                </Typography>
                              )}
                            </Box>
                          </>
                        ) : (
                          // Компактный вид для месяца
                          <Box>
                            {dayData.stats.companies > 0 && (
                              <Chip 
                                label={dayData.stats.companies} 
                                size="small"
                                icon={<CompanyIcon sx={{ fontSize: '14px !important' }} />}
                                sx={{ 
                                  height: 20, 
                                  fontSize: '0.65rem',
                                  background: '#88c0d0',
                                  color: '#fff',
                                  '& .MuiChip-icon': { color: '#fff' }
                                }} 
                              />
                            )}
                            {dayData.stats.tasks > 0 && (
                              <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.5 }}>
                                {dayData.stats.completedTasks}/{dayData.stats.tasks} задач
                              </Typography>
                            )}
                          </Box>
                        )}
                      </Box>
                    ) : (
                      calendarViewMode === 'week' && (
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                          Нет записей
                        </Typography>
                      )
                    )}
                  </Paper>
                );
              })}
            </Box>
          </Paper>
        ) : (
        // Режим доски - Kanban
        <Box 
          sx={{ 
            display: 'flex', 
            gap: 2.5, 
            overflowX: 'auto', 
            pb: 2, 
            minHeight: '70vh', 
            alignItems: 'flex-start',
          }}
        >
          {selectedBoard.columns.sort((a, b) => a.order - b.order).map((column, colIndex) => (
            <Paper
              key={column.id}
              sx={{
                minWidth: 360,
                maxWidth: 360,
                background: isDarkMode ? 'rgba(22, 27, 34, 0.6)' : '#f8fafc',
                backdropFilter: isDarkMode ? 'blur(20px)' : 'none',
                border: '1px solid',
                borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.08)',
                borderRadius: 3,
                display: 'flex',
                flexDirection: 'column',
                animation: `fadeInUp 0.4s ease ${colIndex * 0.1}s both`,
                '@keyframes fadeInUp': {
                  '0%': { opacity: 0, transform: 'translateY(20px)' },
                  '100%': { opacity: 1, transform: 'translateY(0)' },
                },
              }}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, column.id)}
            >
              {/* Column Header */}
              <Box
                sx={{
                  p: 2,
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    left: 16,
                    right: 16,
                    height: 3,
                    background: column.color,
                    borderRadius: 2,
                    boxShadow: `0 0 20px ${column.color}40`,
                  }
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 700, 
                      whiteSpace: 'nowrap', 
                      overflow: 'hidden', 
                      textOverflow: 'ellipsis', 
                      flex: 1,
                      color: 'text.primary',
                    }}
                  >
                    {column.title}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label={cards.filter(c => c.columnId === column.id).length}
                      size="small"
                      sx={{ 
                        fontWeight: 700, 
                        background: column.color,
                        color: '#ffffff',
                        boxShadow: `0 4px 12px ${column.color}40`,
                      }}
                    />
                    <IconButton 
                      size="small" 
                      onClick={() => handleDeleteColumn(column.id)} 
                      sx={{ 
                        color: 'text.secondary',
                        '&:hover': { color: '#bf616a' }
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </Box>

              {/* Cards */}
              <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5, flex: 1, overflow: 'auto' }}>
                {cards
                  .filter(card => card.columnId === column.id)
                  .sort((a, b) => a.position - b.position)
                  .map((card) => (
                    <Card
                      key={card._id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, card)}
                      sx={{
                        cursor: 'grab',
                        background: card.color || (isDarkMode ? 'rgba(22, 27, 34, 0.8)' : '#ffffff'),
                        backdropFilter: isDarkMode ? 'blur(10px)' : 'none',
                        borderLeft: `4px solid ${priorityColors[card.priority]}`,
                        border: '1px solid',
                        borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.08)',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: isDarkMode 
                            ? `0 8px 24px rgba(0, 0, 0, 0.4), 0 0 20px ${priorityColors[card.priority]}20`
                            : `0 8px 24px rgba(0, 0, 0, 0.12)`,
                        },
                        '&:active': { cursor: 'grabbing' }
                      }}
                    >
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1, color: 'text.primary' }}>
                            {card.title || card.company?.name || 'Без названия'}
                          </Typography>
                          <IconButton 
                            size="small" 
                            onClick={(e) => handleOpenCardMenu(e, card)}
                            sx={{ color: 'text.secondary' }}
                          >
                            <MoreIcon fontSize="small" />
                          </IconButton>
                        </Box>

                        {card.description && (
                          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
                            {card.description}
                          </Typography>
                        )}

                        <Divider sx={{ my: 1.5 }} />

                        <Stack spacing={1}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CompanyIcon fontSize="small" sx={{ color: '#88c0d0' }} />
                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                              {card.company?.name}
                            </Typography>
                          </Box>

                          {card.district && (
                            <Chip
                              size="small"
                              label={`📍 ${card.district}`}
                              sx={{
                                background: isDarkMode ? 'rgba(136, 192, 208, 0.15)' : 'rgba(94, 129, 172, 0.1)',
                                border: '1px solid',
                                borderColor: isDarkMode ? 'rgba(136, 192, 208, 0.3)' : 'rgba(94, 129, 172, 0.3)',
                                color: isDarkMode ? '#88c0d0' : '#5e81ac',
                                fontWeight: 600,
                                width: 'fit-content',
                              }}
                            />
                          )}

                          {card.dealAmount && (
                            <Chip
                              size="small"
                              label={`💰 ${new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB', maximumFractionDigits: 0 }).format(card.dealAmount)}`}
                              sx={{
                                background: isDarkMode ? 'rgba(163, 190, 140, 0.15)' : 'rgba(163, 190, 140, 0.1)',
                                border: '1px solid rgba(163, 190, 140, 0.3)',
                                color: '#a3be8c',
                                fontWeight: 700,
                                width: 'fit-content',
                              }}
                            />
                          )}
                          
                          {card.company?.phone && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <PhoneIcon fontSize="small" sx={{ color: '#a3be8c' }} />
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                {card.company.phone}
                              </Typography>
                            </Box>
                          )}

                          {card.company?.email && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <EmailIcon fontSize="small" sx={{ color: '#ebcb8b' }} />
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                {card.company.email}
                              </Typography>
                            </Box>
                          )}

                          {card.date && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <CalendarIcon fontSize="small" sx={{ color: '#5e81ac' }} />
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                {new Date(card.date).toLocaleDateString('ru-RU', { weekday: 'short', day: 'numeric', month: 'short' })}
                              </Typography>
                            </Box>
                          )}

                          {card.assignedTo && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <PersonIcon fontSize="small" sx={{ color: '#b48ead' }} />
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                {card.assignedTo.firstName} {card.assignedTo.lastName}
                              </Typography>
                            </Box>
                          )}
                        </Stack>

                        {card.stickers && card.stickers.length > 0 && (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1.5 }}>
                            {card.stickers.map((sticker) => (
                              <Chip
                                key={sticker._id}
                                label={sticker.text}
                                size="small"
                                onDelete={() => handleDeleteSticker(card._id, sticker._id)}
                                sx={{
                                  background: `${sticker.color}30`,
                                  border: `1px solid ${sticker.color}`,
                                  color: sticker.color,
                                  fontWeight: 600,
                                  fontSize: '0.75rem',
                                  height: 24,
                                  '& .MuiChip-deleteIcon': { color: sticker.color }
                                }}
                              />
                            ))}
                          </Box>
                        )}

                        {card.tasks && card.tasks.length > 0 && (
                          <Box 
                            sx={{ 
                              mt: 1.5, 
                              p: 1.5, 
                              background: isDarkMode ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.03)', 
                              borderRadius: 2,
                              border: '1px solid',
                              borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.06)',
                            }}
                          >
                            <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5, display: 'block' }}>
                              Задачи ({card.tasks.filter(t => t.completed).length}/{card.tasks.length})
                            </Typography>
                            {card.tasks.map((task) => (
                              <Box key={task._id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                <Checkbox
                                  size="small"
                                  checked={task.completed}
                                  onChange={() => handleToggleTask(card, task._id, task.completed)}
                                  sx={{ 
                                    p: 0,
                                    color: '#88c0d0',
                                    '&.Mui-checked': { color: '#a3be8c' }
                                  }}
                                />
                                <Typography
                                  variant="caption"
                                  sx={{
                                    flex: 1,
                                    textDecoration: task.completed ? 'line-through' : 'none',
                                    color: task.completed ? 'text.secondary' : 'text.primary'
                                  }}
                                >
                                  {task.text}
                                </Typography>
                                <IconButton size="small" onClick={() => handleOpenEditTask(card, task)}>
                                  <EditIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                </IconButton>
                                <IconButton size="small" onClick={() => handleDeleteTask(card._id, task._id)}>
                                  <CloseIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                </IconButton>
                              </Box>
                            ))}
                          </Box>
                        )}

                        {card.notes && card.notes.length > 0 && (
                          <Box 
                            sx={{ 
                              mt: 1.5, 
                              p: 1.5, 
                              background: 'rgba(235, 203, 139, 0.1)', 
                              borderRadius: 2, 
                              borderLeft: '3px solid #ebcb8b' 
                            }}
                          >
                            <Typography variant="caption" sx={{ fontWeight: 600, color: '#ebcb8b', mb: 0.5, display: 'block' }}>
                              Заметки ({card.notes.length})
                            </Typography>
                            {card.notes.map((note) => (
                              <Box key={note._id} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 0.5 }}>
                                <NotesIcon sx={{ fontSize: 14, color: '#ebcb8b', mt: 0.2 }} />
                                <Typography variant="caption" sx={{ flex: 1, color: 'text.primary' }}>
                                  {note.text}
                                </Typography>
                                <IconButton size="small" onClick={() => handleDeleteNote(card._id, note._id)}>
                                  <CloseIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                </IconButton>
                              </Box>
                            ))}
                          </Box>
                        )}

                        {/* Card actions */}
                        <Box sx={{ display: 'flex', gap: 0.5, mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.08)' }}>
                          <Tooltip title="Добавить стикер">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSelectedCard(card);
                                setOpenStickerDialog(true);
                              }}
                              sx={{ 
                                background: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)',
                                '&:hover': { background: 'rgba(136, 192, 208, 0.2)' }
                              }}
                            >
                              <StickerIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Добавить задачу">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSelectedCard(card);
                                setOpenTaskDialog(true);
                              }}
                              sx={{ 
                                background: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)',
                                '&:hover': { background: 'rgba(163, 190, 140, 0.2)' }
                              }}
                            >
                              <TaskIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Добавить заметку">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSelectedCard(card);
                                setOpenNoteDialog(true);
                              }}
                              sx={{ 
                                background: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)',
                                '&:hover': { background: 'rgba(235, 203, 139, 0.2)' }
                              }}
                            >
                              <NotesIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Chip
                            label={card.priority}
                            size="small"
                            sx={{
                              ml: 'auto',
                              background: `${priorityColors[card.priority]}30`,
                              border: `1px solid ${priorityColors[card.priority]}`,
                              color: priorityColors[card.priority],
                              fontWeight: 600,
                              fontSize: '0.7rem',
                              height: 24
                            }}
                          />
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
              </Box>

              {/* Add card button */}
              <Box 
                sx={{ 
                  p: 1.5, 
                  borderTop: '1px solid',
                  borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.08)',
                }}
              >
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenCardDialog(column.id)}
                  sx={{ 
                    textTransform: 'none', 
                    fontWeight: 600,
                    borderStyle: 'dashed',
                  }}
                >
                  Добавить карточку
                </Button>
              </Box>
            </Paper>
          ))}
        </Box>
        )
      ) : (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" sx={{ color: 'text.secondary' }}>
            Нет досок
          </Typography>
        </Box>
      )}

      {/* Dialogs */}
      <Dialog open={openCardDialog} onClose={() => setOpenCardDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {selectedCard ? 'Редактировать карточку' : 'Новая карточка'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Заголовок (опционально)"
            value={cardForm.title}
            onChange={(e) => setCardForm({ ...cardForm, title: e.target.value })}
            margin="normal"
            placeholder="Если не указан, будет использовано название компании"
          />
          <TextField
            fullWidth
            label="Описание (опционально)"
            value={cardForm.description}
            onChange={(e) => setCardForm({ ...cardForm, description: e.target.value })}
            margin="normal"
            multiline
            rows={2}
          />
          <Autocomplete
            fullWidth
            options={companies}
            getOptionLabel={(option) => option.name || ""}
            value={companies.find(c => c._id === cardForm.company) || null}
            onChange={(e, newValue) => setCardForm({ ...cardForm, company: newValue ? newValue._id : "" })}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Компания"
                margin="normal"
                required
                placeholder="Начните вводить название..."
              />
            )}
            isOptionEqualToValue={(option, value) => option._id === value._id}
            noOptionsText="Компания не найдена"
          />
          <TextField
            fullWidth
            label="Район"
            value={cardForm.district}
            onChange={(e) => setCardForm({ ...cardForm, district: e.target.value })}
            margin="normal"
            required
            placeholder="Например: Центральный, Северный, Южный..."
          />
          <TextField
            fullWidth
            type="number"
            label="Сумма сделки (₽)"
            value={cardForm.dealAmount}
            onChange={(e) => setCardForm({ ...cardForm, dealAmount: e.target.value })}
            margin="normal"
            placeholder="Введите сумму сделки"
            InputProps={{ inputProps: { min: 0, step: 1000 } }}
          />
          <TextField
            fullWidth
            select
            label="Приоритет"
            value={cardForm.priority}
            onChange={(e) => setCardForm({ ...cardForm, priority: e.target.value })}
            margin="normal"
          >
            <MenuItem value="низкий">Низкий</MenuItem>
            <MenuItem value="средний">Средний</MenuItem>
            <MenuItem value="высокий">Высокий</MenuItem>
            <MenuItem value="критический">Критический</MenuItem>
          </TextField>
          <TextField
            fullWidth
            type="date"
            label="Дата"
            value={cardForm.date}
            onChange={(e) => setCardForm({ ...cardForm, date: e.target.value })}
            margin="normal"
            InputLabelProps={{ shrink: true }}
            required
          />
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>Цвет карточки</Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {cardColors.map((color) => (
                <Box
                  key={color}
                  onClick={() => setCardForm({ ...cardForm, color })}
                  sx={{
                    width: 40,
                    height: 40,
                    backgroundColor: color,
                    border: cardForm.color === color ? '3px solid #88c0d0' : '2px solid',
                    borderColor: cardForm.color === color ? '#88c0d0' : (isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'),
                    borderRadius: 2,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    boxShadow: cardForm.color === color ? '0 0 20px rgba(136, 192, 208, 0.4)' : 'none',
                    '&:hover': { transform: 'scale(1.1)' }
                  }}
                />
              ))}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenCardDialog(false)} variant="outlined">Отмена</Button>
          <Button 
            onClick={handleSaveCard} 
            variant="contained"
            disabled={!cardForm.company || !cardForm.district || !cardForm.date}
          >
            Сохранить
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openStickerDialog} onClose={() => setOpenStickerDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Добавить стикер</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Текст стикера"
            value={stickerForm.text}
            onChange={(e) => setStickerForm({ ...stickerForm, text: e.target.value })}
            margin="normal"
          />
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>Цвет</Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {stickerColors.map((color) => (
                <Box
                  key={color}
                  onClick={() => setStickerForm({ ...stickerForm, color })}
                  sx={{
                    width: 40,
                    height: 40,
                    backgroundColor: color,
                    border: stickerForm.color === color ? '3px solid' : '2px solid',
                    borderColor: stickerForm.color === color ? (isDarkMode ? '#fff' : '#000') : 'rgba(128,128,128,0.2)',
                    borderRadius: 2,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    boxShadow: stickerForm.color === color ? `0 0 20px ${color}60` : 'none',
                    '&:hover': { transform: 'scale(1.1)' }
                  }}
                />
              ))}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenStickerDialog(false)} variant="outlined">Отмена</Button>
          <Button onClick={handleAddSticker} variant="contained">Добавить</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openTaskDialog} onClose={() => { setOpenTaskDialog(false); setEditingTask(null); setTaskForm({ text: '', dueDate: '' }); }} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{editingTask ? 'Редактировать задачу' : 'Добавить задачу'}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Текст задачи"
            value={taskForm.text}
            onChange={(e) => setTaskForm({ ...taskForm, text: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            type="date"
            label="Срок"
            value={taskForm.dueDate}
            onChange={(e) => setTaskForm({ ...taskForm, dueDate: e.target.value })}
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => { setOpenTaskDialog(false); setEditingTask(null); setTaskForm({ text: '', dueDate: '' }); }} variant="outlined">Отмена</Button>
          <Button onClick={handleAddTask} variant="contained">{editingTask ? 'Сохранить' : 'Добавить'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openNoteDialog} onClose={() => setOpenNoteDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Добавить заметку</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Текст заметки"
            value={noteForm.text}
            onChange={(e) => setNoteForm({ ...noteForm, text: e.target.value })}
            margin="normal"
            multiline
            rows={3}
            placeholder="Введите текст заметки..."
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenNoteDialog(false)} variant="outlined">Отмена</Button>
          <Button onClick={handleAddNote} variant="contained">Добавить</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openColumnDialog} onClose={() => setOpenColumnDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Добавить колонку</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Название колонки"
            value={columnForm.title}
            onChange={(e) => setColumnForm({ ...columnForm, title: e.target.value })}
            margin="normal"
          />
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>Цвет заголовка</Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {['#88c0d0', '#a3be8c', '#ebcb8b', '#bf616a', '#b48ead', '#5e81ac', '#d08770', '#8fbcbb'].map((color) => (
                <Box
                  key={color}
                  onClick={() => setColumnForm({ ...columnForm, color })}
                  sx={{
                    width: 40,
                    height: 40,
                    backgroundColor: color,
                    border: columnForm.color === color ? '3px solid' : '2px solid',
                    borderColor: columnForm.color === color ? (isDarkMode ? '#fff' : '#000') : 'rgba(128,128,128,0.2)',
                    borderRadius: 2,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    boxShadow: columnForm.color === color ? `0 0 20px ${color}60` : 'none',
                    '&:hover': { transform: 'scale(1.1)' }
                  }}
                />
              ))}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenColumnDialog(false)} variant="outlined">Отмена</Button>
          <Button onClick={handleAddColumn} variant="contained">Добавить</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openBoardDialog} onClose={() => setOpenBoardDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Создать новую доску</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Название доски"
            value={boardForm.name}
            onChange={(e) => setBoardForm({ ...boardForm, name: e.target.value })}
            margin="normal"
            placeholder="Например: Воронка продаж Q1 2025"
            autoFocus
          />
          <Typography variant="caption" sx={{ color: 'text.secondary', mt: 2, display: 'block' }}>
            Доска будет создана со стандартными колонками.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenBoardDialog(false)} variant="outlined">Отмена</Button>
          <Button onClick={handleCreateBoard} variant="contained" disabled={!boardForm.name}>
            Создать
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openEditBoardDialog} onClose={() => setOpenEditBoardDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Переименовать доску</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Название доски"
            value={editBoardName}
            onChange={(e) => setEditBoardName(e.target.value)}
            margin="normal"
            placeholder={selectedBoard?.name}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenEditBoardDialog(false)} variant="outlined">Отмена</Button>
          <Button onClick={handleRenameBoard} variant="contained" disabled={!editBoardName}>
            Сохранить
          </Button>
        </DialogActions>
      </Dialog>

      <Menu
        anchorEl={cardMenuAnchor}
        open={Boolean(cardMenuAnchor)}
        onClose={handleCloseCardMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={handleEditFromMenu}>
          <ListItemIcon>
            <EditIcon fontSize="small" sx={{ color: '#88c0d0' }} />
          </ListItemIcon>
          <ListItemText>Редактировать</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleOpenCopyDialog}>
          <ListItemIcon>
            <CopyIcon fontSize="small" sx={{ color: '#a3be8c' }} />
          </ListItemIcon>
          <ListItemText>Копировать на другую дату</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleOpenChangeDateDialog}>
          <ListItemIcon>
            <CalendarIcon fontSize="small" sx={{ color: '#ebcb8b' }} />
          </ListItemIcon>
          <ListItemText>Изменить дату</ListItemText>
        </MenuItem>
        {isAdmin && getAvailableBoardsForMove().length > 0 && (
          <MenuItem onClick={handleOpenMoveDialog}>
            <ListItemIcon>
              <MoveIcon fontSize="small" sx={{ color: '#5e81ac' }} />
            </ListItemIcon>
            <ListItemText>Перенести в другую доску</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={async () => {
          if (menuCard) {
            try {
              await axios.patch(`/api/cards/${menuCard._id}/archive`);
              await fetchBoardData(selectedBoard._id, selectedDate);
              handleCloseCardMenu();
            } catch (error) {
              console.error('Ошибка архивирования карточки:', error);
            }
          }
        }}>
          <ListItemIcon>
            <ArchiveIcon fontSize="small" sx={{ color: '#b48ead' }} />
          </ListItemIcon>
          <ListItemText>Убрать в архив</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDeleteFromMenu}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" sx={{ color: '#bf616a' }} />
          </ListItemIcon>
          <ListItemText sx={{ color: '#bf616a' }}>Удалить</ListItemText>
        </MenuItem>
      </Menu>

      <Dialog open={openMoveDialog} onClose={() => { setOpenMoveDialog(false); setCardToMove(null); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Перенести карточку в другую доску</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            Выберите доску и колонку, куда нужно перенести карточку
          </Typography>
          <TextField
            fullWidth
            select
            label="Целевая доска"
            value={moveTargetBoard}
            onChange={(e) => {
              setMoveTargetBoard(e.target.value);
              setMoveTargetColumn('');
            }}
            margin="normal"
          >
            {getAvailableBoardsForMove().map((board) => (
              <MenuItem key={board._id} value={board._id}>
                {board.name} ({board.manager?.firstName} {board.manager?.lastName})
              </MenuItem>
            ))}
          </TextField>
          {moveTargetBoard && (
            <TextField
              fullWidth
              select
              label="Целевая колонка"
              value={moveTargetColumn}
              onChange={(e) => setMoveTargetColumn(e.target.value)}
              margin="normal"
            >
              {getTargetBoardColumns().map((column) => (
                <MenuItem key={column.id} value={column.id}>
                  {column.title}
                </MenuItem>
              ))}
            </TextField>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => { setOpenMoveDialog(false); setCardToMove(null); }} variant="outlined">Отмена</Button>
          <Button 
            onClick={handleMoveCard} 
            variant="contained"
            disabled={!moveTargetBoard || !moveTargetColumn}
          >
            Перенести
          </Button>
        </DialogActions>
      </Dialog>

      {/* Диалог копирования карточки */}
      <Dialog open={openCopyDialog} onClose={() => { setOpenCopyDialog(false); setCardToCopy(null); }} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Копировать карточку на другую дату</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            {cardToCopy && `Копирование карточки: ${cardToCopy.company?.name || cardToCopy.title}`}
          </Typography>
          <TextField
            fullWidth
            type="date"
            label="Дата для копии"
            value={copyTargetDate}
            onChange={(e) => setCopyTargetDate(e.target.value)}
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => { setOpenCopyDialog(false); setCardToCopy(null); }} variant="outlined">Отмена</Button>
          <Button 
            onClick={handleCopyCard} 
            variant="contained"
            disabled={!copyTargetDate}
            sx={{
              background: 'linear-gradient(135deg, #a3be8c 0%, #88c0d0 100%)',
            }}
          >
            Копировать
          </Button>
        </DialogActions>
      </Dialog>

      {/* Диалог изменения даты карточки */}
      <Dialog open={openChangeDateDialog} onClose={() => { setOpenChangeDateDialog(false); setCardToCopy(null); }} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Изменить дату карточки</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            {cardToCopy && `Карточка: ${cardToCopy.company?.name || cardToCopy.title}`}
          </Typography>
          <TextField
            fullWidth
            type="date"
            label="Новая дата"
            value={newCardDate}
            onChange={(e) => setNewCardDate(e.target.value)}
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => { setOpenChangeDateDialog(false); setCardToCopy(null); }} variant="outlined">Отмена</Button>
          <Button 
            onClick={handleChangeCardDate} 
            variant="contained"
            disabled={!newCardDate}
            sx={{
              background: 'linear-gradient(135deg, #ebcb8b 0%, #d08770 100%)',
            }}
          >
            Изменить дату
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default Kanban;
